import { getContractByName } from "eth/contracts/contracts";
import { Contract, ethers } from "ethers";
import { action, autorun, observable, runInAction } from "mobx";
import EthStore from "./EthStore";
import handleTransaction from "helpers/handleTransaction";
import UserStore from "./UserStore";
import VaultStore from "./VaultStore";
import NFTStore from "./NFTStore";
import { formatResult } from "helpers/utils";

type Currency = "ETH" | "YGY";

class RouterStore {
  @observable randomReady: boolean = false;
  @observable contract: Contract | any = null;
  @observable storage: Contract | any = null;
  @observable currentRegeneratorTax = 0;
  @observable userEthDeposited = 0;
  @observable lastUserDepositRound = 0;
  @observable userLevel = 0;
  @observable lotteryInfo = {
    currentLotteryRound: 0,
    currentRNG: 0,
  };
  @observable lotteryTickets = {
    total: 0,
    userTotal: 0,
    userActive: 0,
  };

  constructor() {
    autorun(this.syncToStorage);
  }

  syncToStorage = () => {
    if (localStorage) {
      localStorage.setItem("ygy-randomready", JSON.stringify(this.randomReady));
      localStorage.setItem("ygy-lottery-ticket-count", JSON.stringify(this.lotteryTickets));
    }
  };

  @action initializeContract = (network: string) => {
    const router = getContractByName("Router", network);
    const storage = getContractByName("Storage", network);
    if (router) {
      this.contract = new ethers.Contract(router.address, router.abi, EthStore.signer);
      this.storage = new ethers.Contract(storage.address, storage.abi, EthStore.signer);
    } else {
      console.error("Could not find find contract, wrong network supplied?");
    }
  };

  @action initialize = async () => {
    await this.getLotteryRound();
    await this.getRandomResult();
    await this.getRegeneratorTax();
    await this.getLotteryTicketCounts();
    await this.getEthDeposited();
    await this.getUserLevel();
  };

  @action getEthDeposited = async () => {
    const userEthDeposited = formatResult(await this.storage.liquidityContributedEthValue(EthStore.account));
    runInAction(() => (this.userEthDeposited = Number(userEthDeposited.toFixed(4))));
  };
  @action getUserLevel = async () => {
    const userLevel = await this.storage.lastTicketLevel(EthStore.account);
    runInAction(() => (this.userLevel = Number(userLevel)));
  };
  @action getRegeneratorTax = async () => {
    const regeneratorTax = Number(await this.contract.regeneratorTax());
    runInAction(() => (this.currentRegeneratorTax = regeneratorTax));
  };

  @action getRandomResult = async () => {
    const randomResult = await this.contract.lotteryResults(this.lastUserDepositRound);
    runInAction(() => (this.randomReady = randomResult > 0));
  };

  @action claimLink = async () => {
    await handleTransaction(async () => await this.contract.claimLink(), `LINK NFT claim`);
    await NFTStore.getUnclaimed();
    await NFTStore.fetchNFTBalances();
  };

  @action claimRobot = async () => {
    await handleTransaction(async () => await this.contract.claimRobot(), `ROBOT NFT claim`);
    await NFTStore.getUnclaimed();
    await NFTStore.fetchNFTBalances();
  };

  @action claimTickets = async () => {
    await handleTransaction(async () => await this.contract.claimTickets(), `ticket claim`);
    await this.getLotteryTicketCounts();
    await this.getRandomResult();
    await NFTStore.fetchNFTBalances();
  };

  @action getLotteryTicketCounts = async () => {
    const totalTickets = await this.contract.ticketCount();
    const userActiveTickets = await this.contract.userTopTicketStack(EthStore.account);
    const userTotalTickets = await this.contract.userTicketCount(EthStore.account);
    runInAction(() => (this.lotteryTickets.total = Number(totalTickets)));
    runInAction(() => (this.lotteryTickets.userActive = Number(userActiveTickets)));
    runInAction(() => (this.lotteryTickets.userTotal = Number(userTotalTickets)));
  };

  @action getLotteryRound = async (deposit: boolean = false) => {
    const roundNumber = await this.contract.lotteryRoundCounter();
    runInAction(() => (this.lotteryInfo.currentLotteryRound = Number(roundNumber)));
    if (deposit) runInAction(() => (this.lastUserDepositRound = Number(roundNumber)));
  };

  @action async addLiquidity(currency: Currency, amount: number, autoStake: boolean) {
    const parsedAmount = ethers.utils.parseUnits(amount.toString());
    const parsedYgyAmount = ethers.utils.parseUnits(amount.toString(), 6);

    if (currency === "ETH") {
      await handleTransaction(
        async () =>
          await this.contract.addLiquidityETHOnly(EthStore.account, autoStake, {
            value: parsedAmount,
            gasLimit: autoStake ? 825000 : 825000,
          }),
        `conversion of ${currency}`,
        amount
      );

      await this.getLotteryRound(true);
      await this.getLotteryTicketCounts();
      await UserStore.fetchAllBalances();
      await this.getEthDeposited();
      await this.getRandomResult();
      await this.getUserLevel();
      await NFTStore.getUnclaimed();
      await VaultStore.updateCallback("pool-base");
      return;
    }

    if (currency === "YGY") {
      await handleTransaction(
        async () => await this.contract.addLiquidityYGYOnly(parsedYgyAmount, autoStake, { gasLimit: autoStake ? 825000 : 825000 }),
        `conversion of ${currency}`,
        amount
      );
      await this.getLotteryRound(true);
      await this.getLotteryTicketCounts();
      await UserStore.fetchAllBalances();
      await this.getEthDeposited();
      await this.getUserLevel();
      await this.getRandomResult();
      await NFTStore.getUnclaimed();
      await VaultStore.updateCallback("pool-base");
      return;
    }
  }

  @action triggerLottery = async () => {
    await handleTransaction(async () => await this.contract.triggerLottery(), `lottery trigger`);
    await this.getLotteryTicketCounts();
    await this.getRandomResult();
    await NFTStore.fetchNFTBalances();
  };

  @action selfRequestRandomNumber = async () => {
    const seed = ethers.utils.parseEther(Math.floor(Math.random() * 100000000).toString());
    await handleTransaction(async () => this.contract.selfRequestRandomNumber(seed), `generating a random number`);
    await this.getLotteryRound();
    await this.getRandomResult();
    await NFTStore.getUnclaimed();
    await VaultStore.updateCallback("pool-base");
  };
}

export default new RouterStore();
